/**
 * Send focus to something that has just been revealed, and keep trying until it takes.
 *
 * `.focus()` on an element the browser still treats as hidden is a silent no-op — it returns
 * normally and focus stays where it was. A menu panel is shown a frame or more after the state
 * that opens it (`x-show`, or a class), so "open it, then focus its first item on the next tick"
 * is a race: it wins almost always on a quiet machine and loses often enough on a loaded CI runner
 * to be the single flakiest thing in the Playwright lane.
 *
 * It lost silently, too. Every call site wrote `items[0]?.focus()`, so a panel that was not ready
 * produced no focus, no error and no retry: the menu sat open with focus on `<body>`, and the only
 * symptom was a keyboard user whose arrow keys did nothing. `insights/dropdown.js` already names
 * this hazard — it avoids `x-show` for exactly this reason — but the two row menus never got the
 * same treatment.
 *
 * Retrying across animation frames, rather than waiting a fixed delay, is deliberate: how many
 * frames a reveal needs is not knowable from here, so any constant is both too short under load
 * and wasted everywhere else. The attempt is bounded so an element that can never take focus —
 * detached, `disabled`, `display:none` for good — cannot spin forever, and says so when it gives
 * up instead of failing mute.
 */
export function focusOnceShown(element, { frames = 10 } = {}) {
    if (!element || typeof element.focus !== "function" || typeof document === "undefined") {
        return;
    }

    // No rAF (jsdom, a headless shell): one direct attempt is the best that can be made, and is
    // what the old call sites did anyway.
    if (typeof requestAnimationFrame !== "function") {
        element.focus();

        return;
    }

    const attempt = (left) => {
        element.focus();

        if (document.activeElement === element) {
            return;
        }

        if (left <= 0) {
            // Not thrown: losing focus must not take down the press that opened the menu. Visible,
            // though — this used to be the silent half of the bug.
            console.warn("Insights: focus never landed on", element);

            return;
        }

        requestAnimationFrame(() => attempt(left - 1));
    };

    attempt(frames);
}
